import { useState } from 'react';

/**
 * React component that generates an SVG S-shaped curved line (using a cubic bezier curve) with a label centered over it.
 * The curve is designed to start and end with horizontal tangents.
 * The label is only visible when the cursor hovers over the line.
 *
 * @param {object} props - The component props.
 * @param {number} props.x1 - The x-coordinate of the starting point.
 * @param {number} props.y1 - The y-coordinate of the starting point.
 * @param {number} props.x2 - The x-coordinate of the ending point.
 * @param {number} props.y2 - The y-coordinate of the ending point.
 * @param {string} props.label - The text label to display over the line.
 * @param {number} [props.curveHorizontalExtent=50] - Controls the horizontal distance of the control points from the start/end points, influencing the curve's shape. Higher values mean a wider curve.
 * @param {string} [props.lineStroke="#0891b2"] - The color of the line stroke (optional, defaults to black).
 * @param {number} [props.lineStrokeWidth=2] - The width of the line stroke (optional, defaults to 2).
 * @param {string} [props.lineDasharray] - Controls the pattern of dashes and gaps in the line (e.g., "5, 5" for a dashed line).
 * @param {string} [props.labelFill="#0891b2"] - The color of the label text (optional, defaults to black).
 * @param {string} [props.labelFontSize="12px"] - The font size of the label text (optional, defaults to 12px).
 * @param {string} [props.labelVerticalOffset="3px"] - The vertical offset of the label from the line (optional, defaults to 3px). Use a positive value to move the label down, negative to move it up.
 * @param {string} [props.type] - The type of the SVG line: after or parallel
 * @returns {JSX.Element} A group element containing the path and text elements.
 */
const SVGSShapedLineWithLabel = ({
  x1,
  y1,
  x2,
  y2,
  label,
  curveHorizontalExtent = 50,
  lineStroke = '#0891b2',
  lineStrokeWidth = 2,
  lineDasharray = [2, 3], // New prop for dash pattern
  labelFill = '#0891b2',
  labelFontSize = '12px',
  labelVerticalOffset = '-3px', // New prop for vertical offset
  type,
}) => {
  const [isHovered] = useState(false); // State to track hover

  // To ensure horizontal tangents at the start and end points
  // the y-coordinate of the first control point (c1) must be y1,
  // and the y-coordinate of the second control point (c2) must be y2.

  // Calculate the x-coordinates of the control points.
  // We'll offset them horizontally from the start/end points.
  // The direction of the S-curve depends on the relative positions of start and end points.
  // We can use the sign of (y2 - y1) to determine the direction of the horizontal offset for the S shape.
  const yDiff = y2 - y1;
  const xOffset = Math.sign(yDiff) * curveHorizontalExtent;

  // First control point (near start): x is offset, y is y1
  const c1x = type === 'parallel' ? -xOffset / 2 : xOffset;
  const c1y = 0;

  // Second control point (near end): x is offset in the opposite direction, y is y2
  const c2x = x2 - x1 - xOffset;
  const c2y = y2 - y1;

  // Define the path data for a cubic bezier curve
  // M x1 y1 - Move to the start point
  // C c1x c1y c2x c2y x2 y2 - Cubic bezier curve to (x2, y2) using control points (c1x, c1y) and (c2x, c2y)
  const pathData = `M ${0} ${0} C ${c1x} ${c1y} ${c2x} ${c2y} ${x2 - x1} ${y2 - y1}`;

  // Generate a unique ID for the path to be referenced by textPath
  const pathId = `s-shaped-path-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <svg
      width="200"
      height="120"
      overflow="visible"
      className="test absolute"
      style={{
        transform: `translate(${x1}px, ${y1}px)`,
      }}
    >
      {/* SVG Path for the S-shaped line */}
      <path
        id={pathId}
        d={pathData}
        fill="none" // No fill for the line
        stroke={lineStroke}
        strokeWidth={lineStrokeWidth}
        strokeDasharray={lineDasharray} // Added strokeDasharray prop
      />
      <path
        // onMouseEnter={() => {
        //   setIsHovered(true);
        // }}
        // onMouseLeave={() => {
        //   setIsHovered(false);
        // }}
        id={pathId + '2'}
        d={pathData}
        fill="none" // No fill for the line
        stroke={'transparent'}
        strokeWidth={10}
      />

      {/* SVG Text with textPath for the label - Conditionally rendered based on hover */}
      {isHovered && (
        <text
          fill={labelFill}
          fontSize={labelFontSize}
          textAnchor="middle"
          dominantBaseline="text-after-edge"
          dy={labelVerticalOffset}
          style={{}}
        >
          <textPath
            href={`#${pathId}`}
            startOffset="50%"
          >
            {label}
          </textPath>
        </text>
      )}
    </svg>
  );
};

export default SVGSShapedLineWithLabel;
